import { ThemeProvider } from '@/app/providers/ThemeProvider';
import HomePage from '@/pages/HomePage';
import LoginPage from '@/pages/LoginPage';
import Unauthorized from '@/pages/Unauthorized';
import Forbidden from '@/pages/Forbidden';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import React from 'react';
import { useAuth } from '@/app/providers/AuthProvider';
import { LoadingScreen } from '@/components/LoadingScreen';
import { MaintenanceGate } from '@/components/maintenance/MaintenanceGate';

/**
 * Componente App principale che gestisce l'applicazione
 * Implementa:
 * - Routing con React Router
 * - Gestione tema con ThemeProvider
 * - Protezione route con autenticazione e autorizzazione
 * - Pagina di blocco per utenti non autorizzati
 */
function RequireAuth({ children }: { children: React.ReactElement }) {
  const { user, loading, unauthorized } = useAuth();
  const location = useLocation();

  // Mostra loader durante la verifica
  if (loading) {
    return <LoadingScreen message="Verifica autenticazione..." fullScreen={true} />;
  }

  // Utente non autorizzato - mostra pagina di blocco
  if (unauthorized) {
    return <Unauthorized email={unauthorized.email} reason={unauthorized.reason as any} />;
  }

  // Utente non autenticato - redirect al login
  if (!user) {
    const redirectTo = encodeURIComponent(`${location.pathname}${location.search}` || '/');
    return <Navigate to={`/login?redirectTo=${redirectTo}`} replace />;
  }

  // Utente autorizzato - mostra contenuto
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <MaintenanceGate>
          <Routes>
            {/* Maintenance page as default */}
            {/* <Route path="/" element={<MaintenancePage />} /> */}
            {/* HomePage acts as layout with sidebar for all authenticated routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forbidden" element={<Forbidden />} />
            <Route path="/*" element={<RequireAuth><HomePage /></RequireAuth>} />
          </Routes>
        </MaintenanceGate>
      </BrowserRouter>
      </ThemeProvider>
  );
}

export default App;